import { NEW_BLOCK_ID } from '@unifyapps/defs/constants';
import type { BlockId, BlockType } from '@unifyapps/defs/types/block';
import type { Event } from '@unifyapps/defs/types/event';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import _reduce from 'lodash/reduce';
import { EXPORT_FOOTER_ID, EXPORT_HEADER_ID, FOOTER_ID, HEADER_ID, ROOT_ID } from '../const';
import { BlockDataAttributes } from '../hooks/useBlockDataAttributes';
import { isRuntimeBlockId } from './runTimeBlocks';

/**
 * Checks if the block doesn't exist
 * @param blockId -- The block id
 *
 * stringified null is used when extracting blockId from data attributes
 *
 * `null` check is done for backward compatibility
 */
export const isEmptyBlock = (blockId: BlockId | undefined) =>
  blockId === null || blockId === NEW_BLOCK_ID || blockId === 'null';

/**
 * Returns key for react, we're using blockId and index to make it unique, since blockId can be null
 * @param blockId - block id to be rendered
 * @param index - index of the block
 */
export const getBlockKey = (blockId: BlockId, index: number) =>
  isEmptyBlock(blockId) ? index : blockId;

export function getIsRootBlock(blockId: BlockId): boolean {
  return blockId === ROOT_ID;
}

export const getIsRenameAllowed = (blockId: BlockId) =>
  blockId !== ROOT_ID && blockId !== HEADER_ID && blockId !== FOOTER_ID;

/**
 * Returns all the events from all the blocks
 * @param blocks -- The blocks
 */
export const getAllBlockEvents = (blocks: Record<string, BlockType>) =>
  _reduce(
    blocks,
    (acc, block) => {
      const { events } = block;
      if (events) {
        acc.push(...events);
      }
      return acc;
    },
    [] as Event[],
  );

const ANCHOR_ID_PREFIX = 'anchorKey_';
/**
 * Doubled so the owner/sub-element boundary survives sub-element ids that contain a single `_`
 * themselves — TanStack cell ids are `${rowId}_${columnId}`, and rowId is a primary key value.
 */
const ANCHOR_ID_SEPARATOR = '__';

export const isAnchorId = (id: string) => id.startsWith(ANCHOR_ID_PREFIX);

/**
 * @param ownerBlockId - block that owns the sub-element, used to scope re-anchoring
 * @param subElementId - opaque address of the sub-element within that block
 */
export const buildAnchorId = (ownerBlockId: BlockId, subElementId: string) =>
  `${ANCHOR_ID_PREFIX}${ownerBlockId}${ANCHOR_ID_SEPARATOR}${subElementId}`;

/** Owning block of an anchor id, or undefined for any other id kind. */
export const getAnchorOwnerId = (id: string): string | undefined => {
  if (!isAnchorId(id)) return undefined;
  const [ownerBlockId] = id.slice(ANCHOR_ID_PREFIX.length).split(ANCHOR_ID_SEPARATOR);
  return ownerBlockId || undefined;
};

/**
 * Ids reaching this are not all developer-authored: an anchor id embeds a row's primary-key
 * value, and a runtime block id embeds the same. Escaping is what keeps a value like
 * `Acme "Rockets" Inc` from throwing SyntaxError out of querySelector, or a crafted one from
 * widening the selector into a list that matches an unrelated element.
 */
export const getBlockElementSelector = (blockId: string) => {
  let attribute: BlockDataAttributes;
  if (isAnchorId(blockId)) {
    attribute = BlockDataAttributes.BlockAnchorId;
  } else if (isRuntimeBlockId(blockId)) {
    attribute = BlockDataAttributes.BlockRunTimeBlockId;
  } else {
    attribute = BlockDataAttributes.BlockId;
  }

  return `[${attribute}="${CSS.escape(blockId)}"]`;
};

/**
 * Finds a block element by its ID within the provided page element(s).
 * @param targetBlockId - The ID of the block to find
 * @param pageEl - The primary element to search within
 * @param fallbackPageEl - Optional fallback element to search if block not found in pageEl
 * @returns The found block element or null
 */
export const getTargetBlockElement = ({
  targetBlockId = '',
  pageEl,
  fallbackPageEl,
}: {
  targetBlockId: string | undefined;
  pageEl: HTMLElement | null;
  /** Fallback element to search if block is not found in pageEl */
  fallbackPageEl?: HTMLElement | null;
}) => {
  const selector = getBlockElementSelector(targetBlockId);

  // First, try to find the element in pageEl
  if (pageEl) {
    const element = pageEl.querySelector(selector);
    if (element) return element;
  }

  // If not found in pageEl and fallbackPageEl is provided, search in fallbackPageEl
  if (fallbackPageEl) {
    return fallbackPageEl.querySelector(selector);
  }

  return null;
};

/** Matches `computeDescendantsUnionRect`: one non-zero dimension is enough to count as a box. */
const hasLayoutBox = (element: Element) => {
  const { width, height } = element.getBoundingClientRect();
  return width > 0 || height > 0;
};

/**
 * Returns the element to measure or anchor against. TemplateComponent puts `data-block-id` on a
 * `display: contents` wrapper, which generates no box, so its own rect is 0x0 at the origin.
 * Falls back to the first descendant that has a box, which also skips hidden or empty children
 * and needs no depth limit, since template wrappers nest up to `SLOT_DEPTH_CAP`.
 *
 * Anchoring is the case that bites. A popover on a zero-area rect lands at the viewport origin,
 * and floating-ui's `referenceHidden` reads that as fully clipped and closes it with nothing
 * logged.
 *
 * Returns a single element, so a block with several roots anchors to the first of them rather
 * than their combined extent. Where a rect is enough, `computeDescendantsUnionRect` in
 * `generateLoaderBlocks` covers the union case.
 */
export const getLayoutBoxElement = (element: Element): Element => {
  if (hasLayoutBox(element)) return element;

  for (const descendant of element.querySelectorAll('*')) {
    if (hasLayoutBox(descendant)) return descendant;
  }

  return element;
};

type OmitKeys<T, K extends keyof T> = Omit<T, K>;
type PickKeys<T, K extends keyof T> = Pick<T, K>;

export const omitKeysFromEntity = <T extends Record<string, unknown>, K extends keyof T>(
  entity: object | undefined,
  keys: K[],
): OmitKeys<T, K> => {
  return _omit(entity, keys) as OmitKeys<T, K>;
};

export const pickKeysFromEntity = <T extends Record<string, unknown>, K extends keyof T>(
  entity: object | undefined,
  keys: K[],
): PickKeys<T, K> => {
  return _pick(entity, keys) as PickKeys<T, K>;
};

export const omitKeysFromEntities = <T extends Record<string, unknown>, K extends keyof T>(
  entities: Record<string, T | undefined>,
  keys: K[],
): Record<string, OmitKeys<T, K>> => {
  const result: Record<string, OmitKeys<T, K>> = {};

  for (const [key, entity] of Object.entries(entities)) {
    result[key] = omitKeysFromEntity(entity, keys);
  }

  return result;
};

export const getIsReservedBlockId = (blockId: BlockId) => {
  return (
    blockId === ROOT_ID ||
    blockId === HEADER_ID ||
    blockId === FOOTER_ID ||
    blockId === EXPORT_HEADER_ID ||
    blockId === EXPORT_FOOTER_ID
  );
};

export const getReservedBlockDisplayName = (blockId: BlockId) => {
  if (blockId === ROOT_ID) return 'body';
  if (blockId === HEADER_ID) return 'header';
  if (blockId === FOOTER_ID) return 'footer';
  if (blockId === EXPORT_HEADER_ID) return 'exportPageHeader';
  if (blockId === EXPORT_FOOTER_ID) return 'exportPageFooter';
  return undefined;
};
